import dayjs from '../../util/configuredDayJs';

// This is a performance constant, not just a UX choice: the shared Table renders the whole
// dataSource on a fresh mount (itemCount is undefined until then, so slice(0, itemCount) is a
// no-op), and a cold mount always pays that cost regardless of memoization. Raising this value
// scales the first row build and first full-array DOM render linearly.
export const TASK_LIST_PAGE_SIZE = 200;

// The list renders task type, area, work date and assignee. It reads the assignee from the
// separately-loaded user list and the plot names from the separately-loaded client list, so the
// only relation it actually needs populated is `site`.
const LIST_INCLUDE_FIELDS = 'site';

// The Excel export is the only consumer of the heavy relations, so it — and only it — pays for them.
// `user` is deliberately excluded: the export is unbounded and populates one `user` object per task
// row, so a single account with a large base64 profilePicture would send that blob once per row
// (potentially gigabytes across thousands of tasks). The export instead fetches users once and looks
// up the assignee by id, the same pattern the list already uses (see HandleTaskData.js).
const EXPORT_INCLUDE_FIELDS = 'plots,chemical,fertilizer,site,client';

// Filter values arrive either as a JS Date (from the antd DatePicker) or as a string (from the URL
// query params), so normalize both.
const startOfDayIso = (value) => dayjs(value).startOf('day').toISOString();
const endOfDayIso = (value) => dayjs(value).endOf('day').toISOString();

const appendFilters = (parts, { beginDateFilter, endDateFilter, areaFilter, taskTypeFilter } = {}) => {
  if (beginDateFilter) {
    parts.push(`workDate[$gte]=${encodeURIComponent(startOfDayIso(beginDateFilter))}`);
  }
  if (endDateFilter) {
    parts.push(`workDate[$lte]=${encodeURIComponent(endOfDayIso(endDateFilter))}`);
  }
  if (areaFilter) {
    parts.push(`clientId=${encodeURIComponent(areaFilter)}`);
  }
  if (taskTypeFilter) {
    parts.push(`parameters.taskType=${encodeURIComponent(taskTypeFilter)}`);
  }
  return parts;
};

// Bounded, lean, newest EDITED first. Asks for one row MORE than we display: the response is a bare
// array with no total (the service runs paginate:false), so the 201st row is the only signal that
// older tasks exist.
//
// Sorted by updatedAt, not workDate: workDate is backdated in this dataset (newest workDate can be
// weeks behind newest createdAt), and this window is capped at 200 rows. Sorting the window by
// workDate would let a task the user just entered with an old work date fall outside the 200-row
// window entirely — invisible in the default view. Sorting by updatedAt guarantees a just-created or
// just-edited task is always at the top, and matches what users saw before this query was rewritten.
export const buildTaskListQuery = (filters = {}) => {
  const parts = [`includeFields=${LIST_INCLUDE_FIELDS}`, '$sort[updatedAt]=-1', `$limit=${TASK_LIST_PAGE_SIZE + 1}`];
  return appendFilters(parts, filters).join('&');
};

// Unbounded on purpose: the user asked for this file and is watching a spinner.
// $limit is OMITTED, never set to -1 — the API coerces -1 to 1 (Math.abs(parseInt(n))).
//
// Sorted by workDate, NOT updatedAt — deliberately asymmetric with the list above. The list's sort
// decides WHICH 200 rows are visible, so it must guarantee new entries make the cut. The export is
// unbounded and contains everything regardless of sort, so its sort only decides row order in the
// file, and work-date order is the natural reading order for a work report.
export const buildTaskExportQuery = (filters = {}) => {
  const parts = [`includeFields=${EXPORT_INCLUDE_FIELDS}`, '$sort[workDate]=-1'];
  return appendFilters(parts, filters).join('&');
};
